<template>
  <div>
    <form
      @submit.prevent="saveCell"
      @reset.prevent="discardCell"
      @keydown.esc="discardCell"
    >
      <v-container v-if="editMode">
        <v-row>
          <v-text-field
            v-if="editProperties.type === CellEditType.STRING"
            ref="stringInput"
            v-model="cellValue"
            density="compact"
            :maxlength="editProperties.maxlength"
            :style="editProperties.width && `width: ${editProperties.width}px`"
            class="editable-element"
            hide-details="auto"
            @click.stop
          />
          <v-text-field
            v-if="editProperties.type === CellEditType.NUMBER"
            ref="numberInput"
            v-model="cellValue"
            type="number"
            density="compact"
            :style="editProperties.width && `width: ${editProperties.width}px`"
            :step="editProperties.step"
            :min="editProperties.min"
            :max="editProperties.max"
            hide-details="auto"
            class="editable-element edit-number"
            @click.stop
          />
          <!-- TODO: Adjust displaying date picker in the table -->
          <v-date-picker
            v-if="editProperties.type === CellEditType.DATE"
            v-model="cellValue"
            placeholder="Click to select..."
            :date-formatter="(date) => date.toLocaleString()"
            @click.stop
          />
          <div class="d-flex align-center">
            <v-btn
              icon="mdi-check"
              density="compact"
              class="editable-element"
              type="submit"
            />
            <v-btn
              icon="mdi-close"
              density="compact"
              class="editable-element"
              type="reset"
            />
          </div>
        </v-row>
      </v-container>
    </form>
    <a v-if="!editMode" @click.stop="editCell()">
      <span v-if="editProperties.type === CellEditType.STRING">{{
        cellValue
      }}</span>
      <span v-else-if="editProperties.type === CellEditType.NUMBER">{{
        formatNumber(cellValue)
      }}</span>
      <span v-else-if="editProperties.type === CellEditType.DATE">{{
        cellValue.toLocaleString()
      }}</span>
      <v-btn icon="mdi-pencil" color="primary" variant="text" />
    </a>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

import { CellEditType } from '@/models/core/base'

type EditableCellProps = {
  value: string | number | Date
  editProperties?: EditPropsType
}

interface EditPropsType {
  type?: CellEditType
  min?: number
  max?: number
  step?: number
  digits?: number
  maxlength?: number
  width?: number
}

const emit = defineEmits(['save'])
const props = withDefaults(defineProps<EditableCellProps>(), {
  editProperties: () => ({
    type: CellEditType.STRING,
  }),
})

const cellValue = ref(props.value)
const originalCellValue = ref(JSON.parse(JSON.stringify(props.value)))
const editMode = ref(false)
const stringInput = ref(null)
const numberInput = ref(null)

watch(
  () => props.value,
  () => {
    cellValueChanged()
  }
)

function cellValueChanged() {
  cellValue.value = props.value
  originalCellValue.value = JSON.parse(JSON.stringify(props.value))
  editMode.value = false
}

function editCell() {
  editMode.value = true
  if (props.editProperties.type === CellEditType.NUMBER) {
    setTimeout(() => {
      numberInput.value.focus()
    }, 150)
  } else if (props.editProperties.type === CellEditType.STRING) {
    setTimeout(() => {
      stringInput.value.focus()
    }, 150)
  }
}

function discardCell() {
  if (props.editProperties.type === CellEditType.DATE) {
    cellValue.value = new Date(
      JSON.parse(JSON.stringify(originalCellValue.value))
    )
  } else {
    cellValue.value = JSON.parse(JSON.stringify(originalCellValue.value))
  }
  editMode.value = false
}

function saveCell() {
  editMode.value = false
  originalCellValue.value = JSON.parse(JSON.stringify(cellValue.value))
  emit('save', cellValue.value)
}

function formatNumber(n: string): string {
  return parseFloat(n).toFixed(props.editProperties.digits || 0)
}
</script>

<style lang="scss" scoped>
// In some cases, the cell will overlap over another cell (e.g. narrow view) and we want
// the cell still to be editable
.editable-element {
  z-index: 10;
  margin-left: 0.5rem;
}

.edit-number {
  width: 100px;
}
</style>
